import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { v4 as uuid } from 'uuid';
import DrawerMenuRight from '../../Common/DrawerMenuRight';
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import DateTimeLocalInput from '../../Common/DateTimeLocalInput';
import { localToUtcForStorage } from '../../../utils/dateUtils';
import { getNumberInputDisplayValue, parseNumberInputValue } from '../../../utils/numberInputUtils';
import { ServiceLocator } from '../../../Services/ServiceLocator';

const normalizeStatus = (status) => {
    if (!status) return null;
    const normalized = status.toString().toLowerCase();
    if (normalized === 'fulfilled' || normalized === 'fulfill') return 'Fulfilled';
    if (normalized === 'pending') return 'Pending';
    if (normalized === 'cancelled' || normalized === 'canceled') return 'Cancelled';
    if (normalized === 'shipped') return 'Shipped';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const emptyEntry = () => ({
    id: uuid(),
    part: null,
    quantity: 1,
});

const initialFormState = () => ({
    newLogDate: new Date(),
    newLogDateManuallyChanged: false,
    newLogSource: 'Customer Service',
    newLogOrderNumber: '',
    newLogStatus: 'Fulfilled',
    newLogReason: '',
    newLogCustomerName: '',
    newLogShippingAddress: '',
    newLogCustomerEmail: '',
    newLogAffectsProjections: true,
    manualLogEntries: [emptyEntry()],
    saving: false,
});

/**
 * Isolated drawer so typing in text fields does not re-render the heavy Sales page/table.
 */
class ManualSalesLogDrawer extends Component {
    state = initialFormState();

    shouldComponentUpdate(nextProps, nextState) {
        // Skip parent-driven re-renders while closed; while open, ignore unstable partsOptions identity.
        if (!this.props.open && !nextProps.open) return false;
        if (nextState !== this.state) return true;
        if (nextProps.open !== this.props.open) return true;
        if (nextProps.salesLogSources !== this.props.salesLogSources) return true;
        if (nextProps.classes !== this.props.classes) return true;
        if (nextProps.assemblies !== this.props.assemblies) return true;
        if (nextProps.subAssemblies !== this.props.subAssemblies) return true;
        if (nextProps.parts !== this.props.parts) return true;
        // partsOptions is rebuilt every Sales render — only refresh if length changes
        if ((nextProps.partsOptions || []).length !== (this.props.partsOptions || []).length) return true;
        return false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState(initialFormState());
        }
    }

    hasUnsavedChanges = () => {
        const {
            newLogSource,
            newLogOrderNumber,
            newLogReason,
            newLogCustomerName,
            newLogShippingAddress,
            newLogCustomerEmail,
            manualLogEntries,
        } = this.state;
        return (
            newLogSource !== 'Customer Service'
            || newLogOrderNumber !== ''
            || newLogReason !== ''
            || newLogCustomerName !== ''
            || newLogShippingAddress !== ''
            || newLogCustomerEmail !== ''
            || manualLogEntries.some((entry) => entry.part || String(entry.quantity) !== '1')
        );
    };

    handlePartChange = (index, newValue) => {
        const entries = [...this.state.manualLogEntries];
        entries[index] = { ...entries[index], part: newValue };
        if (index === entries.length - 1 && newValue) {
            entries.push(emptyEntry());
        }
        this.setState({ manualLogEntries: entries }, () => {
            if (!newValue) return;
            setTimeout(() => {
                const quantityField = document.querySelector(
                    `[data-manual-entry-index="${index}"] input[type="number"]`
                );
                if (quantityField) {
                    quantityField.focus();
                    quantityField.select();
                }
            }, 100);
        });
    };

    handleQuantityChange = (index, event) => {
        const entries = [...this.state.manualLogEntries];
        entries[index] = {
            ...entries[index],
            quantity: event.target.value,
        };
        this.setState({ manualLogEntries: entries });
    };

    handleQuantityKeyDown = (index, event) => {
        if (event.key !== 'Enter' && event.key !== 'Tab') return;
        event.stopPropagation();

        if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey)) {
            event.preventDefault();
            const { manualLogEntries } = this.state;
            if (index === manualLogEntries.length - 1 && manualLogEntries[index].part) {
                const entries = [...manualLogEntries, emptyEntry()];
                this.setState({ manualLogEntries: entries }, () => {
                    setTimeout(() => {
                        const nextPartField = document.querySelector(`[data-manual-entry-index="${index + 1}"] input`);
                        if (nextPartField) nextPartField.focus();
                    }, 100);
                });
            } else if (index < manualLogEntries.length - 1) {
                setTimeout(() => {
                    const nextPartField = document.querySelector(`[data-manual-entry-index="${index + 1}"] input`);
                    if (nextPartField) nextPartField.focus();
                }, 100);
            }
        }
    };

    removeEntry = (index) => {
        let entries = this.state.manualLogEntries.filter((_, i) => i !== index);
        if (entries.length === 0) entries = [emptyEntry()];
        if (!entries.some((e) => !e.part)) {
            entries = [...entries, emptyEntry()];
        }
        this.setState({ manualLogEntries: entries });
    };

    handleSubmit = () => {
        const { assemblies, subAssemblies, parts, onCreated, onClose } = this.props;
        const validEntries = this.state.manualLogEntries.filter((entry) => entry.part);
        if (validEntries.length === 0) {
            return window.$alertWarning('You must select at least one part for this log');
        }

        this.setState({ saving: true });

        const payloads = validEntries.map((entry, index) => {
            const assembly = assemblies.find((x) => x.assembly.id === entry.part.value);
            const subAssembly = subAssemblies.find((x) => x.subAssembly.id === entry.part.value);
            const part = parts.find((x) => x.part.id === entry.part.value);

            return {
                source: this.state.newLogSource,
                quantity: parseNumberInputValue(entry.quantity),
                reason: this.state.newLogReason || '',
                orderNumber: this.state.newLogOrderNumber.length > 1 ? this.state.newLogOrderNumber : '',
                partId: part ? part.part.id : null,
                assemblyId: assembly ? assembly.assembly.id : null,
                subAssemblyId: subAssembly ? subAssembly.subAssembly.id : null,
                logDate: this.state.newLogDateManuallyChanged
                    ? localToUtcForStorage(this.state.newLogDate)
                    : localToUtcForStorage(new Date()),
                affectsProjections: this.state.newLogAffectsProjections,
                status: normalizeStatus(this.state.newLogStatus),
                isManuallyAssigned: true,
                lineNumber: index + 1,
                customerName: this.state.newLogCustomerName || null,
                shippingAddress: this.state.newLogShippingAddress || null,
                customerEmail: this.state.newLogCustomerEmail || null,
            };
        });

        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        assemblyService.createManualSalesLogs(payloads)
            .then(() => {
                window.$alertSuccess(`${validEntries.length} sales log${validEntries.length > 1 ? 's' : ''} created`);
                if (onCreated) onCreated(validEntries);
                this.setState(initialFormState());
                if (onClose) onClose();
            })
            .catch((error) => {
                window.$alertError(`Error creating sales logs: ${error.message}`);
                this.setState({ saving: false });
            });
    };

    render() {
        const { open, onClose, salesLogSources, partsOptions, classes } = this.props;
        if (!open) return null;

        const {
            newLogDate,
            newLogSource,
            newLogOrderNumber,
            newLogStatus,
            newLogReason,
            newLogCustomerName,
            newLogShippingAddress,
            newLogCustomerEmail,
            newLogAffectsProjections,
            manualLogEntries,
            saving,
        } = this.state;

        return (
            <DrawerMenuRight
                title="Add Manual Sales Log"
                customWidth={600}
                open={open}
                onClose={onClose}
                hasUnsavedChanges={this.hasUnsavedChanges()}
                onSubmit={false}
            >
                <div style={{overflowX: 'hidden', overflowY: 'auto'}}>
                    <div style={{ marginBottom: 30 }}>
                        <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Order Information</h3>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <DateTimeLocalInput
                                    label="Order Date"
                                    value={newLogDate}
                                    onChange={(parsedDate) => {
                                        this.setState({ newLogDate: parsedDate, newLogDateManuallyChanged: true });
                                    }}
                                    fullWidth
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Source</InputLabel>
                                    <Select
                                        value={newLogSource}
                                        onChange={(e) => this.setState({ newLogSource: e.target.value })}
                                        label="Source"
                                    >
                                        {(salesLogSources || []).map((source) => (
                                            <MenuItem key={source.source} value={source.source}>
                                                {source.source}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <TextField
                                    value={newLogOrderNumber}
                                    onChange={(e) => this.setState({ newLogOrderNumber: e.target.value })}
                                    label="Dealer Order #"
                                    variant="outlined"
                                    fullWidth
                                    autoComplete="off"
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={normalizeStatus(newLogStatus)}
                                        onChange={(e) => this.setState({ newLogStatus: normalizeStatus(e.target.value) })}
                                        label="Status"
                                    >
                                        <MenuItem value="Fulfilled">Fulfilled</MenuItem>
                                        <MenuItem value="Pending">Pending</MenuItem>
                                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div style={{ marginTop: '15px' }}>
                            <TextField
                                value={newLogReason}
                                onChange={(e) => this.setState({ newLogReason: e.target.value })}
                                label="Reason (Optional)"
                                variant="outlined"
                                fullWidth
                                multiline
                                autoComplete="off"
                                inputProps={{ maxLength: 500 }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '15px' }}>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <TextField
                                    value={newLogCustomerName}
                                    onChange={(e) => this.setState({ newLogCustomerName: e.target.value })}
                                    label="Customer Name"
                                    variant="outlined"
                                    fullWidth
                                    autoComplete="off"
                                    inputProps={{ maxLength: 200 }}
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <TextField
                                    value={newLogShippingAddress}
                                    onChange={(e) => this.setState({ newLogShippingAddress: e.target.value })}
                                    label="Shipping Address"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    autoComplete="off"
                                    inputProps={{ maxLength: 500 }}
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <TextField
                                    value={newLogCustomerEmail}
                                    onChange={(e) => this.setState({ newLogCustomerEmail: e.target.value })}
                                    label="Customer Email"
                                    variant="outlined"
                                    fullWidth
                                    autoComplete="off"
                                    inputProps={{ maxLength: 255 }}
                                />
                            </div>
                        </div>
                        <center>
                            <div style={{ marginTop: '15px' }}>
                                <Checkbox
                                    onClick={(event) => {
                                        this.setState({ newLogAffectsProjections: event.target.checked });
                                    }}
                                    checked={newLogAffectsProjections}
                                    style={{ padding: 0 }}
                                />
                                {' '}
                                Affects Sales Projections
                            </div>
                        </center>
                    </div>

                    <div style={{ marginBottom: 20 }}>
                        <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Items</h3>
                        {manualLogEntries.map((entry, index) => (
                            <div
                                key={entry.id}
                                data-manual-entry-index={index}
                                style={{
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center',
                                    marginBottom: '10px',
                                    padding: '10px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    backgroundColor: '#fafafa',
                                }}
                            >
                                <div style={{ flex: '2' }}>
                                    <Autocomplete
                                        autoHighlight
                                        options={partsOptions}
                                        onChange={(event, newValue) => this.handlePartChange(index, newValue)}
                                        value={entry.part}
                                        getOptionLabel={(option) => {
                                            if (!option) return '';
                                            if (option.type === 'assembly') {
                                                return `${option.partId} rev ${option.revision}${option.currentlyPackaging ? ' 📦' : ''}${option.currentlySelling ? ' 🛒' : ''}`;
                                            }
                                            if (option.type === 'subassembly' || option.type === 'part') {
                                                return `${option.partId} rev ${option.revision}`;
                                            }
                                            return option.title || '';
                                        }}
                                        getOptionSelected={(option) => option && entry.part && option.value === entry.part.value}
                                        renderOption={(option) => <PartAutocompleteOption option={option} classes={classes} />}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === 'Tab') {
                                                event.stopPropagation();
                                                event.preventDefault();
                                                if (event.shiftKey && event.key === 'Tab') {
                                                    if (index > 0) {
                                                        setTimeout(() => {
                                                            const prevQuantityField = document.querySelector(`[data-manual-entry-index="${index - 1}"] input[type="number"]`);
                                                            if (prevQuantityField) prevQuantityField.focus();
                                                        }, 100);
                                                    }
                                                    return;
                                                }
                                                const quantityField = event.target.closest('[data-manual-entry-index]')?.querySelector('input[type="number"]');
                                                if (quantityField) {
                                                    setTimeout(() => quantityField.focus(), 100);
                                                }
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Assembly / Part"
                                                variant="outlined"
                                                autoComplete="new-password"
                                                size="small"
                                            />
                                        )}
                                    />
                                </div>
                                <div style={{ flex: '1' }}>
                                    <TextField
                                        label="Quantity"
                                        variant="outlined"
                                        type="number"
                                        value={getNumberInputDisplayValue(entry.quantity)}
                                        onChange={(event) => this.handleQuantityChange(index, event)}
                                        onKeyDown={(event) => this.handleQuantityKeyDown(index, event)}
                                        size="small"
                                        fullWidth
                                    />
                                </div>
                                {manualLogEntries.length > 1 && (
                                    <div>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            onClick={() => this.removeEntry(index)}
                                            style={{ minWidth: '40px' }}
                                        >
                                            ×
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        {saving ? (
                            <CircularProgress />
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.handleSubmit}
                                className="success-button-lg"
                                disabled={!manualLogEntries.some((entry) => entry.part)}
                            >
                                Confirm All Entries (
                                {manualLogEntries.filter((entry) => entry.part).length}
                                {' '}
                                items)
                            </Button>
                        )}
                    </div>
                </div>
            </DrawerMenuRight>
        );
    }
}

ManualSalesLogDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
    salesLogSources: PropTypes.array,
    partsOptions: PropTypes.array,
    assemblies: PropTypes.array,
    subAssemblies: PropTypes.array,
    parts: PropTypes.array,
    classes: PropTypes.object,
};

ManualSalesLogDrawer.defaultProps = {
    onCreated: null,
    salesLogSources: [],
    partsOptions: [],
    assemblies: [],
    subAssemblies: [],
    parts: [],
    classes: {},
};

export default ManualSalesLogDrawer;
